import React, { useContext, useMemo } from "react";
import { ThemeContext } from "@/App";

/**
 * Owner split — "who we're waiting on" as ONE bar split by owner plus a key
 * (Tushar, 2-Nov-2026). Shared by the dashboard's Waiting On widget and the
 * top of the Blocked Board, so the two always read as the same thing.
 *
 * rows: [{ key, owner, label, type: external|internal|unassigned, count, oldest, overdue }]
 * already ranked (outside first, then by count / age).
 * Ambers = outside Rockpecker, greys = inside, dashed zinc = no owner yet.
 */
const EXT_SHADES = ["#d97706", "#f59e0b", "#fbbf24", "#fcd34d", "#fde68a"];
// Inside greys run dark→light on the light theme and light→dark on dark, so
// the biggest owner always has the most contrast against the card.
const INT_LIGHT = ["#334155", "#475569", "#64748b", "#94a3b8", "#cbd5e1", "#e2e8f0"];
const INT_DARK  = ["#e2e8f0", "#cbd5e1", "#94a3b8", "#64748b", "#475569", "#334155"];
const NONE_SHADE = "#a1a1aa";

export function useOwnerColours(rows) {
  const { theme } = useContext(ThemeContext) || {};
  return useMemo(() => {
    const intShades = theme === "light" ? INT_LIGHT : INT_DARK;
    const seen = { external: 0, internal: 0 };
    return rows.map((r) => {
      if (r.type === "external") return { ...r, color: EXT_SHADES[Math.min(seen.external++, EXT_SHADES.length - 1)] };
      if (r.type === "internal") return { ...r, color: intShades[Math.min(seen.internal++, intShades.length - 1)] };
      return { ...r, color: NONE_SHADE };
    });
  }, [rows, theme]);
}

/** The bar — each owner's width = its open blocks. `selected` dims the rest. */
export function OwnerSplitBar({ rows, selected = null, height = "h-3.5", testId }) {
  return (
    <div className={`flex gap-[2px] ${height} rounded-full overflow-hidden`} data-testid={testId}>
      {rows.map((r) => (
        <span
          key={r.key}
          title={`${r.owner ? r.owner + " · " : ""}${r.label}: ${r.count} open, oldest ${r.oldest}d`}
          className="transition-opacity duration-150"
          style={{ flex: r.count, background: r.color, opacity: selected && selected !== r.key ? 0.25 : 1 }}
        />
      ))}
    </div>
  );
}

/**
 * The key — swatch, code, owner, count (red = a promise slipped), oldest wait.
 * With `onSelect`, each entry is a button that filters by that owner (Blocked
 * Board); without it, entries are plain (the dashboard widget is one big link).
 */
export function OwnerKey({ rows, selected = null, onSelect, className = "", testIdPrefix = "owner-key" }) {
  return (
    <div className={`grid gap-x-7 gap-y-[7px] ${className}`}>
      {rows.map((r) => {
        const isSel = selected === r.key;
        const dim = selected && !isSel;
        const body = (
          <>
            <span
              className={`w-[9px] h-[9px] rounded-[3px] shrink-0 ${r.type === "unassigned" ? "border border-dashed border-[hsl(var(--muted-foreground))]/70" : ""}`}
              style={r.type === "unassigned" ? undefined : { background: r.color }}
            />
            {r.owner && <span className="font-mono-data font-bold text-[9.5px] tracking-wide text-[hsl(var(--muted-foreground))]">{r.owner}</span>}
            <span className={`truncate ${r.type === "unassigned" ? "text-[hsl(var(--muted-foreground))]" : "text-[hsl(var(--foreground))]"}`}>{r.label}</span>
            <span
              className={`ml-auto font-mono-data text-[12.5px] font-bold tabular-nums ${r.overdue ? "text-red-500" : "text-[hsl(var(--foreground))]"}`}
              title={r.overdue ? `${r.overdue} promise${r.overdue === 1 ? "" : "s"} slipped` : undefined}
            >
              {r.count}
            </span>
            <span className="font-mono-data text-[10.5px] text-[hsl(var(--muted-foreground))] w-8 text-right whitespace-nowrap">{r.oldest}d</span>
          </>
        );
        const base = "flex items-center gap-2 min-w-0 text-[12px]";
        return onSelect ? (
          <button
            key={r.key}
            type="button"
            onClick={() => onSelect(isSel ? null : r.key)}
            aria-pressed={isSel}
            className={`${base} -mx-2 px-2 py-1 rounded-lg text-left transition-[background-color,opacity] duration-150 ${
              isSel ? "bg-[hsl(var(--muted))]/80 ring-1 ring-[hsl(var(--border))]" : "hover:bg-[hsl(var(--muted))]/50"
            } ${dim ? "opacity-50 hover:opacity-100" : ""}`}
            data-testid={`${testIdPrefix}-${r.owner || "none"}`}
          >
            {body}
          </button>
        ) : (
          <div key={r.key} className={base} data-testid={`${testIdPrefix}-${r.owner || "none"}`}>{body}</div>
        );
      })}
    </div>
  );
}
